import { useCallback, useMemo, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useQueryClient } from '@tanstack/react-query';

import { useRoomContext } from '@src/contexts';
import { useNotificationStore, useQuestionAndAnswerStore, useRoomConfigStore, useRoomStore } from '@src/stores';

import { formatTimeStamp } from '@src/utils';

import SocketService from '@src/services/SocketService';

import { queryKeys } from '@src/utils/queryKeys';

import { REPLY_OPTIONS } from './questionBox.constants';

import {
  IQuestion,
  EUserType,
  EAnswerType,
  EQuestionStatus,
  EQuestionAndAnswerSockets,
  IQuestionAndAnswerResponse,
} from '@src/types';

const useQuestionBox = ({
  question: currentQuestion,
  handleOpenAnswerBox,
}: {
  question: IQuestion;
  handleOpenAnswerBox: (answerType: EAnswerType) => void;
}) => {
  const [isMarkingAnswered, setIsMarkingAnswered] = useState(false);
  const [hasMarkedAnsweredOnce, setHasMarkedAnsweredOnce] = useState(false);

  const queryClient = useQueryClient();
  const { speakersList, currentPeerType, currentPeer } = useRoomContext();

  const { setIsQuestionBoxOpened, setQuestionToEdit, questionToEdit, updateQuestion } = useQuestionAndAnswerStore(
    useShallow((state) => ({
      setIsQuestionBoxOpened: state.setIsQuestionBoxOpened,
      setQuestionToEdit: state.setQuestionToEdit,
      questionToEdit: state.questionToEdit,
      updateQuestion: state.updateQuestion,
    }))
  );

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  const { roomId, setInfoModalContent, setIsInfoModalOpen } = useRoomStore(
    useShallow((state) => ({
      roomId: state.roomId,
      setIsInfoModalOpen: state.setIsInfoModalOpen,
      setInfoModalContent: state.setInfoModalContent,
    }))
  );

  const {
    isActionsButtonEnabled,
    isDismissButtonEnabled,
    isAssignQuestionButtonEnabled,
    isDeleteQuestionButtonEnabled,
  } = useRoomConfigStore(
    useShallow((state) => ({
      isActionsButtonEnabled: state.questionsConfig.actionsButton,
      isDismissButtonEnabled: state.questionsConfig.dismissQuestion,
      isAssignQuestionButtonEnabled: state.questionsConfig.assignQuestionButton,
      isDeleteQuestionButtonEnabled: state.questionsConfig.deleteQuestionButton,
    }))
  );

  const { author, formattedTime } = useMemo(() => {
    const author = currentQuestion.anonymous ? null : (currentQuestion.creator?.name ?? null);
    const formattedTime = formatTimeStamp(currentQuestion.createAt);

    return {
      author,
      formattedTime,
    };
  }, [currentQuestion.anonymous, currentQuestion.createAt, currentQuestion.creator?.name]);

  const confirmDeleteQuestion = useCallback(
    async (questionId: string) => {
      try {
        const response: IQuestionAndAnswerResponse = await SocketService.request(
          EQuestionAndAnswerSockets.DELETE_QUESTION,
          {
            questionId,
          }
        );

        if (response?.success) {
          queryClient.invalidateQueries({
            queryKey: [queryKeys.webinar.questions],
          });
          queryClient.invalidateQueries({
            queryKey: [queryKeys.webinar.myQuestions],
          });

          appendNotification({
            title: 'Question Deleted',
            text: 'You successfully deleted your question.',
            source: 'question-and-answer',
          });
        } else {
          console.error('Failed to delete question:', response?.message);
        }
      } catch (err) {
        console.error('Socket delete error:', err);
      } finally {
        setIsInfoModalOpen(false);
      }
    },
    [appendNotification, queryClient, setIsInfoModalOpen]
  );

  const handleDeleteQuestionClick = useCallback(() => {
    if (!currentQuestion) return;

    setInfoModalContent({
      title: `Remove this question!`,
      description: `Are you sure you want to remove the posted question?`,
      buttons: [
        {
          text: 'Cancel',
          onClick: () => {
            setIsInfoModalOpen(false);
            setInfoModalContent(null);
          },
          variant: 'cancel',
        },
        {
          text: 'Remove',
          onClick: () => confirmDeleteQuestion(currentQuestion.id),
          variant: 'primary',
        },
      ],
    });

    setIsInfoModalOpen(true);
  }, [confirmDeleteQuestion, currentQuestion, setInfoModalContent, setIsInfoModalOpen]);

  const generateSpeakerSubMenu = useCallback(
    (answerType: EAnswerType) =>
      speakersList.map((speaker) => ({
        label: speaker.peer_info.peer_name,
        onClick: () => {
          SocketService.request(EQuestionAndAnswerSockets.ASSIGN_QUESTION, {
            questionId: currentQuestion.id,
            toRecordId: speaker.record_id,
            answerType: answerType,
          }).then(() => {
            queryClient.invalidateQueries({
              queryKey: [queryKeys.webinar.questions],
            });
          });
        },
      })),
    [speakersList, currentQuestion.id, queryClient]
  );

  const handleDismissQuestion = useCallback(() => {
    updateQuestion(currentQuestion.id, { status: EQuestionStatus.DISMISSED });
    SocketService.request(EQuestionAndAnswerSockets.DISMISS_QUESTION, {
      questionId: currentQuestion.id,
    }).then(() => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });
    });
  }, [currentQuestion.id, updateQuestion, queryClient]);

  const handleMarkAsAnswered = useCallback(async () => {
    if (isMarkingAnswered || hasMarkedAnsweredOnce) return;
    setIsMarkingAnswered(true);

    try {
      const needsAssignToMeLive =
        currentQuestion.status !== EQuestionStatus.CLOSED &&
        !!currentPeer?.record_id &&
        (currentQuestion.assignTo?.recordId !== currentPeer.record_id ||
          currentQuestion.answer?.answerType !== EAnswerType.LIVE);

      if (needsAssignToMeLive) {
        await SocketService.request(EQuestionAndAnswerSockets.ASSIGN_QUESTION, {
          toRecordId: currentPeer!.record_id,
          questionId: currentQuestion.id,
          answerType: EAnswerType.LIVE,
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.webinar.questions],
        });
      }

      const response: { success: boolean; message?: string; errorCode?: string } = await SocketService.request(
        EQuestionAndAnswerSockets.MARK_QUESTION_AS_ANSWERED,
        {
          questionId: currentQuestion.id,
          roomId,
        }
      );

      const { success, message } = response || {};

      if (success) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.webinar.questions],
        });
        setHasMarkedAnsweredOnce(true);
      } else {
        console.warn(message, { errorCode: response?.errorCode });
      }
    } finally {
      setIsMarkingAnswered(false);
    }
  }, [
    isMarkingAnswered,
    hasMarkedAnsweredOnce,
    currentQuestion.id,
    currentQuestion.status,
    currentQuestion.assignTo?.recordId,
    currentQuestion.answer?.answerType,
    currentPeer,
    roomId,
    queryClient,
  ]);

  const handleClickAssignToMe = useCallback(
    (answerType: EAnswerType) => {
      const questionId = currentQuestion.id;
      if (currentPeer && currentQuestion.status !== EQuestionStatus.CLOSED) {
        SocketService.request(EQuestionAndAnswerSockets.ASSIGN_QUESTION, {
          toRecordId: currentPeer.record_id,
          questionId,
          answerType,
        })
          .then(() =>
            queryClient.invalidateQueries({
              queryKey: [queryKeys.webinar.questions],
            })
          )
          .finally(() => {
            if (answerType !== EAnswerType.LIVE) {
              handleOpenAnswerBox(answerType);
            }
          });
      } else if (currentQuestion.status === EQuestionStatus.CLOSED && answerType !== EAnswerType.LIVE) {
        handleOpenAnswerBox(answerType);
      }
    },
    [currentPeer, currentQuestion.id, currentQuestion.status, handleOpenAnswerBox, queryClient]
  );

  const questionMenuItems = useMemo(
    () => [
      {
        label: 'Edit',
        onClick: () => {
          setQuestionToEdit(currentQuestion);
          setIsQuestionBoxOpened(true);
        },
      },
      ...(isDeleteQuestionButtonEnabled ? [{ label: 'Delete', onClick: handleDeleteQuestionClick }] : []),
    ],
    [
      isDeleteQuestionButtonEnabled,
      handleDeleteQuestionClick,
      setQuestionToEdit,
      currentQuestion,
      setIsQuestionBoxOpened,
    ]
  );

  const questionActions = useMemo(
    () => [
      {
        label: 'Assign to me:',
        showDivider: !isAssignQuestionButtonEnabled,
        actions: REPLY_OPTIONS.map((option) => ({
          label: option.label,
          onClick: () => handleClickAssignToMe(option.value),
        })),
      },
      ...(isAssignQuestionButtonEnabled
        ? [
            {
              label: 'Assign speaker to:',
              actions: REPLY_OPTIONS.map((option) => ({
                label: option.label,
                submenu: generateSpeakerSubMenu(option.value),
              })),
              showDivider: true,
            },
          ]
        : []),
      {
        actions: [
          ...(isDismissButtonEnabled ? [{ label: 'Dismiss question', onClick: handleDismissQuestion }] : []),
          {
            label: 'Mark as answered',
            onClick: handleMarkAsAnswered,
            disabled:
              !(
                currentQuestion.status === EQuestionStatus.OPEN &&
                [EUserType.HOST, EUserType.CO_HOST].includes(currentPeerType!)
              ) ||
              isMarkingAnswered ||
              hasMarkedAnsweredOnce,
          },
        ],
      },
    ],
    [
      currentPeerType,
      currentQuestion.status,
      generateSpeakerSubMenu,
      handleClickAssignToMe,
      handleDismissQuestion,
      handleMarkAsAnswered,
      isAssignQuestionButtonEnabled,
      isDismissButtonEnabled,
      isMarkingAnswered,
      hasMarkedAnsweredOnce,
    ]
  );

  const isEditableQuestion = useMemo(
    () =>
      (currentQuestion.creator.id === currentPeer?.peer_info.peer_id ||
        currentQuestion.creator.uuid === currentPeer?.peer_info.peer_uuid) &&
      currentQuestion.status === EQuestionStatus.OPEN,
    [
      currentPeer?.peer_info.peer_id,
      currentPeer?.peer_info.peer_uuid,
      currentQuestion.creator.id,
      currentQuestion.creator.uuid,
      currentQuestion.status,
    ]
  );

  const showInlineMarkAsAnswered = useMemo(
    () =>
      !hasMarkedAnsweredOnce &&
      currentPeerType === EUserType.SPEAKER &&
      currentQuestion.status === EQuestionStatus.OPEN &&
      currentQuestion.assignTo?.recordId === currentPeer?.record_id &&
      currentQuestion.answer?.answerType === EAnswerType.LIVE,
    [
      hasMarkedAnsweredOnce,
      currentPeerType,
      currentQuestion.status,
      currentQuestion.assignTo?.recordId,
      currentPeer?.record_id,
      currentQuestion.answer?.answerType,
    ]
  );

  return {
    currentPeerType,
    questionToEdit,
    currentPeer,
    author,
    formattedTime,
    questionActions,
    questionMenuItems,
    isEditableQuestion,
    isActionsButtonEnabled,
    handleMarkAsAnswered,
    isMarkingAnswered,
    hasMarkedAnsweredOnce,
    showInlineMarkAsAnswered,
  };
};

export default useQuestionBox;
